import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowDown, ArrowUp, Globe, Heart, Lock, Pencil, Play, Shuffle, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Cover, EqBars } from "../components/Cover";
import { usePlayer } from "../context/PlayerContext";
import { errMsg, http } from "../lib/api";
import { Mosaic } from "../components/PlaylistCard";
import { AuthorLink } from "../components/AuthorLink";

const Icon = ({ testId, onClick, children, danger }) => (
  <button data-testid={testId} onClick={onClick}
    className={`p-2 rounded-lg transition-colors hover:bg-[var(--c-hover)] ${danger ? "text-zinc-500 hover:text-red-400" : "text-zinc-400 hover:text-white"}`}>{children}</button>
);

const PlaylistSongItem = ({ song: s, index: i, songs, owner, onMove, onRemove }) => {
  const p = usePlayer();
  const on = p.current?.id === s.id;
  return (
    <li data-testid={`playlist-song-${s.id}`}
      className={`flex items-center gap-4 p-3 rounded-2xl transition-colors ${on ? "bg-[var(--c-elev)]" : "hover:bg-[var(--c-surface)]"}`}>
      <span className="w-6 text-right font-mono text-xs text-zinc-500">{on && p.playing ? <EqBars /> : i + 1}</span>
      <button data-testid={`playlist-song-play-${s.id}`} onClick={() => (on ? p.toggle() : p.play(s, songs))} className="shrink-0">
        <Cover song={s} className="w-12 h-12 rounded-lg" />
      </button>
      <div className="min-w-0 flex-1">
        <Link to={`/daina/${s.id}`} className="block font-semibold truncate hover:text-[var(--c-orange)] transition-colors">{s.title}</Link>
        <AuthorLink song={s} testId={`playlist-song-author-${s.id}`} className="block text-xs text-zinc-500 truncate" />
      </div>
      {owner && <div className="flex">
        <Icon testId={`playlist-song-up-${s.id}`} onClick={() => i > 0 && onMove(i, -1)}><ArrowUp className="w-4 h-4" /></Icon>
        <Icon testId={`playlist-song-down-${s.id}`} onClick={() => i < songs.length - 1 && onMove(i, 1)}><ArrowDown className="w-4 h-4" /></Icon>
        <Icon testId={`playlist-song-remove-${s.id}`} onClick={() => onRemove(s)} danger><X className="w-4 h-4" /></Icon>
      </div>}
    </li>
  );
};

const usePlaylist = (id) => {
  const nav = useNavigate();
  const [pl, setPl] = useState(null);
  const [missing, setMissing] = useState(false);
  const load = useCallback(() => http.get(`/playlists/${id}`).then((r) => setPl(r.data)).catch(() => setMissing(true)), [id]);
  useEffect(() => { load(); }, [load]);
  const patch = async (body) => setPl((await http.patch(`/playlists/${id}`, body)).data);
  const actions = {
    move: (i, d) => {
      const ids = pl.songs.map((s) => s.id);
      [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
      patch({ song_ids: ids });
    },
    remove: async (s) => { await http.delete(`/playlists/${id}/songs/${s.id}`); load(); },
    rename: () => { const n = window.prompt("Naujas pavadinimas", pl.name); if (n?.trim()) patch({ name: n.trim() }); },
    toggleVisibility: () => patch({ is_public: !pl.is_public }),
    del: async () => {
      if (!window.confirm(`Ištrinti grojaraštį „${pl.name}“?`)) return;
      await http.delete(`/playlists/${id}`);
      toast.success("Grojaraštis ištrintas");
      nav("/grojarasciai");
    },
    like: async () => {
      try { setPl((await http.post(`/playlists/${id}/like`)).data); } catch (e) { toast.error(errMsg(e)); }
    },
  };
  return { pl, missing, actions };
};

const LikeButton = ({ pl, onLike }) => (
  <button data-testid="playlist-like-button" onClick={onLike}
    className={`flex items-center gap-2 px-5 py-3 rounded-full border text-sm transition-colors ${pl.liked ? "border-[var(--c-orange)] text-[var(--c-orange)]" : "border-[var(--c-line)] hover:border-white"}`}>
    <Heart className="w-4 h-4" fill={pl.liked ? "currentColor" : "none"} />{pl.liked ? "Patinka" : "Pamėgti"}
  </button>
);

const OwnerActions = ({ pl, actions }) => (
  <>
    <Icon testId="playlist-rename" onClick={actions.rename}><Pencil className="w-4 h-4" /></Icon>
    <Icon testId="playlist-visibility" onClick={actions.toggleVisibility}>{pl.is_public ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}</Icon>
    <Icon testId="playlist-delete" onClick={actions.del} danger><Trash2 className="w-4 h-4" /></Icon>
  </>
);

const PlayButtons = ({ songs }) => {
  const p = usePlayer();
  return (
    <>
      <button data-testid="playlist-play-all" onClick={() => p.playAll(songs)} disabled={!songs.length}
        className="flex items-center gap-2 px-8 py-3 rounded-full bg-[var(--c-orange)] text-white font-semibold glow-orange hover:bg-[#ff7a26] transition-colors disabled:opacity-50">
        <Play className="w-4 h-4" fill="currentColor" />Groti viską
      </button>
      <button data-testid="playlist-shuffle" onClick={() => p.playAll(songs, true)} disabled={!songs.length}
        className="flex items-center gap-2 px-5 py-3 rounded-full border border-[var(--c-line)] text-sm hover:border-white transition-colors disabled:opacity-50">
        <Shuffle className="w-4 h-4" />Maišyti
      </button>
    </>
  );
};

const PlaylistMeta = ({ pl }) => (
  <p className="text-sm text-zinc-500 mt-2 flex flex-wrap items-center gap-x-2">
    {pl.owner && <Link to={`/autorius/${pl.owner.user_id}`} data-testid="playlist-owner-link" className="text-zinc-300 hover:text-[var(--c-orange)] transition-colors">{pl.owner.name}</Link>}
    {pl.owner && <span>·</span>}<span>{pl.songs.length} dain.</span><span>·</span>
    <span data-testid="playlist-likes" className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" />{pl.likes}</span>
  </p>
);

const PlaylistHeader = ({ pl, actions }) => (
  <header className="flex flex-col sm:flex-row gap-8 sm:items-end">
    <div className="w-48 shrink-0"><Mosaic covers={pl.songs.map((s) => s.cover_path).filter(Boolean)} /></div>
    <div className="min-w-0 flex-1">
      <p className="text-xs uppercase tracking-[0.2em] text-[var(--c-orange)] font-semibold">Grojaraštis{pl.is_public ? "" : " · privatus"}</p>
      <h1 data-testid="playlist-title" className="font-display text-4xl sm:text-5xl font-bold mt-2 break-words">{pl.name}</h1>
      <PlaylistMeta pl={pl} />
      <div className="flex flex-wrap gap-3 mt-6">
        <PlayButtons songs={pl.songs} />
        {pl.is_mine ? <OwnerActions pl={pl} actions={actions} /> : <LikeButton pl={pl} onLike={actions.like} />}
      </div>
    </div>
  </header>
);

export default function PlaylistPage() {
  const { id } = useParams();
  const { pl, missing, actions } = usePlaylist(id);
  if (missing) return <p data-testid="playlist-not-found" className="text-zinc-400">Grojaraštis nerastas. <Link to="/grojarasciai" className="text-[var(--c-orange)]">Grįžti</Link></p>;
  if (!pl) return <div className="h-64 rounded-3xl shimmer" />;
  const { songs } = pl;
  return (
    <div className="max-w-5xl space-y-10 rise">
      <PlaylistHeader pl={pl} actions={actions} />
      {!songs.length && <p data-testid="playlist-empty" className="text-zinc-400">Grojaraštis tuščias. Pridėkite dainų iš <Link to="/atrasti" className="text-[var(--c-orange)]">Atrasti</Link> arba bibliotekos.</p>}
      <ol data-testid="playlist-songs" className="space-y-1">
        {songs.map((s, i) => (
          <PlaylistSongItem key={s.id} song={s} index={i} songs={songs} owner={pl.is_mine}
            onMove={actions.move} onRemove={actions.remove} />
        ))}
      </ol>
    </div>
  );
}
